import { DynamicPage, DynamicPageTag } from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import { diff } from 'deep-diff';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ChipsAutocomplete from '../../components/Common/ChipsAutocomplete';
import Modal, { ModalActionData } from '../../components/Common/Modal';
import { useAuditLog } from '../../contexts/AuditLogContext';
import { useDashboardContext } from '../../contexts/DashboardContext';
import { UserContext } from '../../contexts/UserContext';
import { updateDynamicPage } from '../../services/DynamicPagesService';
import { distinct } from '../../utils';

interface Props {
  dynamicPages: QueryDocumentSnapshot<DynamicPage>[] | string[];
  open: boolean;
  onClose: () => void;
}

export default function UpdateDynamicPagesTagsModal({
  dynamicPages: _dynamicPagesToEdit,
  open,
  onClose,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPages } = useDashboardContext();
  const auditLog = useAuditLog();

  const [addTagsQuery, setAddTagsQuery] = useState<string>('');
  const [addTags, setAddTags] = useState<DynamicPageTag[]>([]);
  const [removeTagsQuery, setRemoveTagsQuery] = useState<string>('');
  const [removeTags, setRemoveTags] = useState<DynamicPageTag[]>([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();

  const tagOptions = useMemo(() => {
    const tagsMap = new Map<string, DynamicPageTag>();
    dynamicPages?.forEach((c) =>
      c.data().tags?.forEach((t) => tagsMap.set(t.id, t)),
    );

    return Array.from(tagsMap.values());
  }, [dynamicPages]);

  useEffect(() => {
    if (!open) return;

    setAddTags([]);
    setRemoveTags([]);
    setSaving(false);
    setError(undefined);
  }, [open]);

  const updateTags = useCallback(async () => {
    if (saving) return;
    if (!selectedAccount) return;

    if (!addTags.length && !removeTags.length) {
      setError(new Error('No tags selected'));
      return;
    }

    setSaving(true);

    try {
      auditLog.log(
        'update',
        'Bulk update dynamic pages tags',
        'dynamicPages',
        undefined,
        undefined,
        diff({}, { addTags, removeTags }),
      );

      const dynamicPagesToEdit = _dynamicPagesToEdit
        .map((d) =>
          typeof d === 'string' ? dynamicPages?.find((p) => p.id === d) : d,
        )
        .filter(Boolean) as QueryDocumentSnapshot<DynamicPage>[];

      for (const dynamicPage of dynamicPagesToEdit) {
        const data = dynamicPage.data();
        const tags = distinct(
          [...(data.tags || []), ...addTags].filter(
            (t) => !removeTags.some((rt) => rt.id === t.id),
          ),
          (a) => a.id,
        );

        await updateDynamicPage(selectedAccount.id, dynamicPage.id, {
          tags,
        });
      }

      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setSaving(false);
  }, [
    _dynamicPagesToEdit,
    addTags,
    auditLog,
    dynamicPages,
    onClose,
    removeTags,
    saving,
    selectedAccount,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: 'Update',
        type: 'primary',
        loading: saving,
        onClick: () => updateTags(),
      },
    ],
    [saving, onClose, updateTags],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions}>
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Update Dynamic Pages Tags
      </DialogTitle>
      <div className="mt-4 flex min-h-48 flex-col gap-4">
        <ChipsAutocomplete<DynamicPageTag>
          title="Add Tags"
          placeholder="Select tags to add"
          getValue={(c) => c?.name}
          selected={addTags}
          onChange={(s) => {
            setAddTags(s);
            setRemoveTags((prev) =>
              prev.filter((t) => !s.find((st) => st.id === t.id)),
            );
          }}
          getOptionId={(t) => t.id}
          options={[
            ...(addTagsQuery.trim().length &&
            tagOptions.every(
              (o) => o.name.toLowerCase() !== addTagsQuery.trim().toLowerCase(),
            )
              ? [{ id: addTagsQuery.trim(), name: addTagsQuery.trim() }]
              : []),
            ...tagOptions,
          ]}
          onInputChange={(v) => setAddTagsQuery(v)}
        />
        <ChipsAutocomplete<DynamicPageTag>
          title="Remove Tags"
          placeholder="Select tags to remove"
          getValue={(c) => c?.name}
          selected={removeTags}
          onChange={(s) => {
            setRemoveTags(s);
            setAddTags((prev) =>
              prev.filter((t) => !s.find((st) => st.id === t.id)),
            );
          }}
          getOptionId={(t) => t.id}
          options={[
            ...(removeTagsQuery.trim().length &&
            tagOptions.every(
              (o) =>
                o.name.toLowerCase() !== removeTagsQuery.trim().toLowerCase(),
            )
              ? [{ id: removeTagsQuery.trim(), name: removeTagsQuery.trim() }]
              : []),
            ...tagOptions,
          ]}
          onInputChange={(v) => setRemoveTagsQuery(v)}
        />
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'An error occurred'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
